import { TeamInvitation, TournamentInvitationWithDetails } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button, EmptyState, LoadingSpinner, PatternBackground, UserCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, RefreshControl, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './InvitationsScreen.styles';

export interface InvitationsScreenProps {
  receivedInvitations: TournamentInvitationWithDetails[];
  sentInvitations: TournamentInvitationWithDetails[];
  teamInvitationsReceived: TeamInvitation[];
  teamInvitationsSent: TeamInvitation[];
  loading: boolean;
  loadingMore: boolean;
  activeTab: 'received' | 'sent';
  typeTab: 'tournaments' | 'teams';
  setActiveTab: (tab: 'received' | 'sent') => void;
  setTypeTab: (tab: 'tournaments' | 'teams') => void;
  onAccept: (invitationId: string) => void;
  onDecline: (invitationId: string) => void;
  onCancel: (invitationId: string) => void;
  onTeamInvitationAccept: (invitationId: string, teamId: string) => void;
  onTeamInvitationDecline: (invitationId: string) => void;
  onTeamInvitationCancel: (invitationId: string) => void;
  onUserPress: (userId: string) => void;
  onTournamentPress: (tournamentId: string) => void;
  onTeamPress: (teamId: string) => void;
  onRefresh: () => void;
  onLoadMoreReceived: () => void;
  onLoadMoreSent: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const InvitationsScreen = ({
  receivedInvitations,
  sentInvitations,
  teamInvitationsReceived,
  teamInvitationsSent,
  loading,
  loadingMore,
  activeTab,
  typeTab,
  setActiveTab,
  setTypeTab,
  onAccept,
  onDecline,
  onCancel,
  onTeamInvitationAccept,
  onTeamInvitationDecline,
  onTeamInvitationCancel,
  onUserPress,
  onTournamentPress,
  onTeamPress,
  onRefresh,
  onLoadMoreReceived,
  onLoadMoreSent,
  colors,
}: InvitationsScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const renderReceivedInvitation = ({ item }: { item: TournamentInvitationWithDetails }) => {
    const isPending = item.status === 'pending';

    return (
      <View style={[styles.invitationCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <TouchableOpacity style={styles.tournamentRow} onPress={() => onTournamentPress(item.tournamentId)}>
          <Ionicons name="trophy-outline" size={18} color={colors.primary} />
          <Text style={[styles.tournamentName, { color: colors.text }]} numberOfLines={1}>
            {item.tournamentName}
          </Text>
          <View style={[styles.typeBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.typeBadgeText, { color: colors.textSecondary }]}>
              {item.tournamentType?.replace('_', ' ')}
            </Text>
          </View>
        </TouchableOpacity>

        <UserCard
          userId={item.inviterId}
          userName={item.userName}
          userEmail={item.userEmail}
          userAvatarUrl={item.userAvatarUrl}
          onPress={() => onUserPress(item.inviterId)}
          colors={colors}
        />

        {item.message && (
          <View style={styles.messageContainer}>
            <Ionicons name="chatbubble-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.message, { color: colors.textSecondary }]}>{item.message}</Text>
          </View>
        )}

        <View style={styles.statusRow}>
          <View style={[styles.statusBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.statusText, { color: colors.text }]}>
              {t(`invitations.status.${item.status}`)}
            </Text>
          </View>
          {isPending && (
            <View style={styles.actions}>
              <Button
                title={t('social.accept')}
                onPress={() => onAccept(item.id)}
                variant="primary"
                size="small"
                colors={colors}
              />
              <Button
                title={t('social.decline')}
                onPress={() => onDecline(item.id)}
                variant="secondary"
                size="small"
                colors={colors}
              />
            </View>
          )}
          {item.status === 'accepted' && (
            <TouchableOpacity
              onPress={() => onTournamentPress(item.tournamentId)}
              style={styles.tournamentLink}
            >
              <Ionicons name="eye-outline" size={16} color={colors.primary} />
              <Text style={[styles.linkText, { color: colors.primary }]}>{t('social.viewTournament')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderSentInvitation = ({ item }: { item: TournamentInvitationWithDetails }) => {
    const isPending = item.status === 'pending';

    return (
      <View style={[styles.invitationCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <TouchableOpacity style={styles.tournamentRow} onPress={() => onTournamentPress(item.tournamentId)}>
          <Ionicons name="trophy-outline" size={18} color={colors.primary} />
          <Text style={[styles.tournamentName, { color: colors.text }]} numberOfLines={1}>
            {item.tournamentName}
          </Text>
          <View style={[styles.typeBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.typeBadgeText, { color: colors.textSecondary }]}>
              {item.tournamentType?.replace('_', ' ')}
            </Text>
          </View>
        </TouchableOpacity>

        <UserCard
          userId={item.inviteeId}
          userName={item.userName}
          userEmail={item.userEmail}
          userAvatarUrl={item.userAvatarUrl}
          onPress={() => onUserPress(item.inviteeId)}
          colors={colors}
        />

        {item.message && (
          <View style={styles.messageContainer}>
            <Ionicons name="chatbubble-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.message, { color: colors.textSecondary }]}>{item.message}</Text>
          </View>
        )}

        <View style={styles.statusRow}>
          <View style={[styles.statusBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.statusText, { color: colors.text }]}>
              {t(`invitations.status.${item.status}`)}
            </Text>
          </View>
          {isPending && (
            <View style={styles.actions}>
              <Button
                title={t('common.cancel')}
                onPress={() => onCancel(item.id)}
                variant="secondary"
                size="small"
                colors={colors}
              />
            </View>
          )}
        </View>
      </View>
    );
  };

  const renderTeamInvitationReceived = ({ item }: { item: TeamInvitation }) => {
    const isPending = item.status === 'pending';
    return (
      <View style={[styles.invitationCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <TouchableOpacity style={styles.tournamentRow} onPress={() => onTeamPress(item.teamId)}>
          <Ionicons name="people-outline" size={18} color={colors.primary} />
          <Text style={[styles.tournamentName, { color: colors.text }]} numberOfLines={1}>
            {item.teamName}
          </Text>
        </TouchableOpacity>
        {item.message && (
          <View style={styles.messageContainer}>
            <Ionicons name="chatbubble-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.message, { color: colors.textSecondary }]}>{item.message}</Text>
          </View>
        )}
        <View style={styles.statusRow}>
          <View style={[styles.statusBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.statusText, { color: colors.text }]}>
              {t(`invitations.status.${item.status}`)}
            </Text>
          </View>
          {isPending && (
            <View style={styles.actions}>
              <Button title={t('social.accept')} onPress={() => onTeamInvitationAccept(item.id, item.teamId)} variant="primary" size="small" colors={colors} />
              <Button title={t('social.decline')} onPress={() => onTeamInvitationDecline(item.id)} variant="secondary" size="small" colors={colors} />
            </View>
          )}
        </View>
      </View>
    );
  };

  const renderTeamInvitationSent = ({ item }: { item: TeamInvitation }) => {
    const isPending = item.status === 'pending';
    return (
      <View style={[styles.invitationCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <TouchableOpacity style={styles.tournamentRow} onPress={() => onTeamPress(item.teamId)}>
          <Ionicons name="people-outline" size={18} color={colors.primary} />
          <Text style={[styles.tournamentName, { color: colors.text }]} numberOfLines={1}>
            {item.teamName}
          </Text>
        </TouchableOpacity>
        {item.message && (
          <View style={styles.messageContainer}>
            <Ionicons name="chatbubble-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.message, { color: colors.textSecondary }]}>{item.message}</Text>
          </View>
        )}
        <View style={styles.statusRow}>
          <View style={[styles.statusBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.statusText, { color: colors.text }]}>
              {t(`invitations.status.${item.status}`)}
            </Text>
          </View>
          {isPending && (
            <View style={styles.actions}>
              <Button title={t('common.cancel')} onPress={() => onTeamInvitationCancel(item.id)} variant="secondary" size="small" colors={colors} />
            </View>
          )}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <PatternBackground>
        <LoadingSpinner />
      </PatternBackground>
    );
  }

  const activeInvitations = activeTab === 'received' ? receivedInvitations : sentInvitations;
  const activeTeamInvitations = activeTab === 'received' ? teamInvitationsReceived : teamInvitationsSent;
  const onLoadMore = activeTab === 'received' ? onLoadMoreReceived : onLoadMoreSent;

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        {/* Type selector: Tournaments / Teams */}
        <View style={[styles.tabs, { borderBottomWidth: 1, borderBottomColor: colors.border }]}>
          <TouchableOpacity
            style={[styles.tab, typeTab === 'tournaments' && { borderBottomColor: colors.primary, borderBottomWidth: 2 }]}
            onPress={() => setTypeTab('tournaments')}
          >
            <Text style={[styles.tabText, { color: typeTab === 'tournaments' ? colors.primary : colors.textSecondary }]}>
              {t('tournaments.tournaments')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, typeTab === 'teams' && { borderBottomColor: colors.primary, borderBottomWidth: 2 }]}
            onPress={() => setTypeTab('teams')}
          >
            <Text style={[styles.tabText, { color: typeTab === 'teams' ? colors.primary : colors.textSecondary }]}>
              {t('teams.teams')}
            </Text>
          </TouchableOpacity>
        </View>
        {/* Received / Sent sub-tabs */}
        <View style={styles.tabs}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'received' && { borderBottomColor: colors.primary, borderBottomWidth: 2 }]}
            onPress={() => setActiveTab('received')}
          >
            <Text style={[styles.tabText, { color: activeTab === 'received' ? colors.primary : colors.textSecondary }]}>
              {t('social.received')} ({typeTab === 'tournaments' ? receivedInvitations.length : teamInvitationsReceived.length})
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'sent' && { borderBottomColor: colors.primary, borderBottomWidth: 2 }]}
            onPress={() => setActiveTab('sent')}
          >
            <Text style={[styles.tabText, { color: activeTab === 'sent' ? colors.primary : colors.textSecondary }]}>
              {t('social.sent')} ({typeTab === 'tournaments' ? sentInvitations.length : teamInvitationsSent.length})
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {typeTab === 'tournaments' ? (
        <FlatList
          data={activeInvitations}
          renderItem={activeTab === 'received' ? renderReceivedInvitation : renderSentInvitation}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          onEndReached={onLoadMore}
          onEndReachedThreshold={0.5}
          refreshControl={<RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />}
          ListFooterComponent={loadingMore ? <LoadingSpinner /> : null}
          ListEmptyComponent={
            <EmptyState
              icon={<Ionicons name="mail-outline" size={52} color={colors.textTertiary} />}
              title={activeTab === 'received' ? t('invitations.noInvitationsReceived') : t('invitations.noInvitationsSent')}
              subtitle={activeTab === 'received' ? t('invitations.whenInvitedAppear') : t('invitations.haventInvitedAnyone')}
              colors={colors}
            />
          }
        />
      ) : (
        <FlatList
          data={activeTeamInvitations}
          renderItem={activeTab === 'received' ? renderTeamInvitationReceived : renderTeamInvitationSent}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          refreshControl={<RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />}
          ListEmptyComponent={
            <EmptyState
              icon={<Ionicons name="people-outline" size={52} color={colors.textTertiary} />}
              title={activeTab === 'received' ? t('invitations.noInvitationsReceived') : t('invitations.noInvitationsSent')}
              subtitle={activeTab === 'received' ? t('invitations.whenInvitedAppear') : t('invitations.haventInvitedAnyone')}
              colors={colors}
            />
          }
        />
      )}
    </PatternBackground>
  );
};
